import React, { useState, useEffect, useCallback, memo } from 'react';
import { supabase } from '../../lib/supabase';

const COLORS = ['#0ea5e9', '#f97316', '#8b5cf6', '#10b981', '#ef4444', '#d97706', '#e11d48', '#0284c7', '#7c3aed', '#15803d', '#1e40af', '#dc2626'];
const TYPES = ['Visa on Arrival', 'e-Visa', 'eNTRI / eVisa', 'Free on Arrival', 'Schengen Visa', 'Sticker Visa', 'B1/B2 Visa', 'TRV (Sticker Visa)', 'Standard Visitor Visa'];
const REGIONS = ['Asia Pacific', 'Middle East', 'Europe', 'Africa', 'Americas'];

const emptyVisa = {
    country: '', region: 'Asia Pacific', flag: '', type: 'e-Visa',
    currency: '', fee: 0, fee_inr: 0,
    processing: '', validity: '', entries: 'Single',
    docs: [], popular: false, color: '#0ea5e9', display_order: 0,
};

/* ─── FLAG IMAGE ─────────────────────────────────────────────────────────── */
const FlagImg = ({ code, size = 40 }) => code ? (
    <img
        src={`https://flagcdn.com/w${size}/${code.toLowerCase()}.png`}
        alt={code}
        style={{ width: size * 1.4, height: size * 0.93, objectFit: 'cover', borderRadius: 4, border: '1px solid #e2e8f0' }}
        onError={e => { e.target.style.display = 'none'; }}
    />
) : (
    <div style={{ width: 40, height: 27, background: '#f1f5f9', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className="fa-solid fa-flag" style={{ color: '#94a3b8', fontSize: 12 }} />
    </div>
);

/* ─── EDIT MODAL (isolated — fast typing) ────────────────────────────────── */
const VisaEditModal = memo(({ initialForm, isNew, onSave, onClose }) => {
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);
    const [newDoc, setNewDoc] = useState('');

    const setF = useCallback((k, v) => setForm(f => ({ ...f, [k]: v })), []);

    const addDoc = useCallback(() => {
        setNewDoc(v => { if (!v.trim()) return v; setForm(f => ({ ...f, docs: [...(f.docs || []), v.trim()] })); return ''; });
    }, []);

    const removeDoc = useCallback((i) => {
        setForm(f => ({ ...f, docs: (f.docs || []).filter((_, idx) => idx !== i) }));
    }, []);

    const updateDoc = useCallback((i, val) => {
        setForm(f => { const docs = [...(f.docs || [])]; docs[i] = val; return { ...f, docs }; });
    }, []);

    const handleSave = async () => {
        setSaving(true);
        // Strip any fields not in the Supabase schema
        const { notes: _notes, ...safeForm } = form;
        await onSave(safeForm, isNew);
        setSaving(false);
    };

    return (
        <div className="admin-modal-overlay" onClick={onClose}>
            <div className="admin-modal" style={{ maxWidth: 740 }} onClick={e => e.stopPropagation()}>
                <div className="admin-modal-header">
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <FlagImg code={form.flag} />
                        <i className="fa-solid fa-passport" style={{ color: 'var(--gold)' }} />
                        {isNew ? 'Add Visa Country' : `Edit — ${form.country}`}
                    </h3>
                    <button className="admin-modal-close" onClick={onClose}><i className="fa-solid fa-xmark" /></button>
                </div>

                <div className="admin-modal-body">
                    {/* Section: Identity */}
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 12 }}>Country Identity</p>
                    <div className="admin-form-grid" style={{ marginBottom: 20 }}>
                        <div className="admin-field">
                            <label className="admin-label">Country Name *</label>
                            <input className="admin-input" value={form.country} onChange={e => setF('country', e.target.value)} placeholder="e.g. Thailand" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Region *</label>
                            <select className="admin-select" value={form.region} onChange={e => setF('region', e.target.value)}>
                                {REGIONS.map(r => <option key={r}>{r}</option>)}
                            </select>
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Flag Code (ISO 2-letter) *</label>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <input className="admin-input" value={form.flag} onChange={e => setF('flag', e.target.value.toUpperCase())} placeholder="e.g. TH" maxLength={2} style={{ flex: 1 }} />
                                <FlagImg code={form.flag} size={40} />
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--text-3)' }}>2-letter ISO code: TH, SG, AE, GB…</span>
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Visa Type</label>
                            <select className="admin-select" value={form.type} onChange={e => setF('type', e.target.value)}>
                                {TYPES.map(t => <option key={t}>{t}</option>)}
                            </select>
                        </div>
                    </div>

                    {/* Section: Details */}
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 12 }}>Visa Details</p>
                    <div className="admin-form-grid" style={{ marginBottom: 20 }}>
                        <div className="admin-field">
                            <label className="admin-label">Processing Time</label>
                            <input className="admin-input" value={form.processing} onChange={e => setF('processing', e.target.value)} placeholder="e.g. 3–5 Working Days" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Validity</label>
                            <input className="admin-input" value={form.validity} onChange={e => setF('validity', e.target.value)} placeholder="e.g. 30 Days" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Entries Allowed</label>
                            <input className="admin-input" value={form.entries} onChange={e => setF('entries', e.target.value)} placeholder="e.g. Single / Multiple" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Currency</label>
                            <input className="admin-input" value={form.currency} onChange={e => setF('currency', e.target.value)} placeholder="e.g. USD" />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Embassy / Govt Fee</label>
                            <input className="admin-input" type="number" value={form.fee} onChange={e => setF('fee', +e.target.value)} />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Fee in ₹ INR</label>
                            <input className="admin-input" type="number" value={form.fee_inr} onChange={e => setF('fee_inr', +e.target.value)} />
                        </div>
                        <div className="admin-field">
                            <label className="admin-label">Display Order</label>
                            <input className="admin-input" type="number" value={form.display_order} onChange={e => setF('display_order', +e.target.value)} />
                        </div>
                        <div className="admin-field">
                            <div className="admin-toggle-row" style={{ height: '100%', alignItems: 'center' }}>
                                <span className="admin-toggle-label">
                                    <i className="fa-solid fa-fire" style={{ color: 'var(--gold)', marginRight: 6 }} />
                                    Show as Popular
                                </span>
                                <input className="admin-toggle" type="checkbox" checked={!!form.popular} onChange={e => setF('popular', e.target.checked)} />
                            </div>
                        </div>

                    </div>

                    {/* Section: Color */}
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 12 }}>Accent Color</p>
                    <div style={{ marginBottom: 20 }}>
                        <div className="admin-color-row">
                            {COLORS.map(c => (
                                <div key={c} className={`admin-color-swatch ${form.color === c ? 'selected' : ''}`}
                                    style={{ background: c }} onClick={() => setF('color', c)} />
                            ))}
                            <input className="admin-input" type="text" value={form.color} onChange={e => setF('color', e.target.value)} style={{ width: 90 }} />
                        </div>
                    </div>

                    {/* Section: Required Documents */}
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 12 }}>Required Documents</p>
                    <div className="admin-list-field" style={{ marginBottom: 4 }}>
                        {(form.docs || []).map((doc, i) => (
                            <div className="admin-list-item" key={i}>
                                <i className="fa-solid fa-check" style={{ color: 'var(--green)', fontSize: 11, flexShrink: 0 }} />
                                <input value={doc} onChange={e => updateDoc(i, e.target.value)} />
                                <button className="admin-btn-icon danger" onClick={() => removeDoc(i)}><i className="fa-solid fa-xmark" /></button>
                            </div>
                        ))}
                        <div className="admin-list-add">
                            <input placeholder="Add a required document…" value={newDoc}
                                onChange={e => setNewDoc(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && addDoc()} />
                            <button className="admin-add-btn" onClick={addDoc}>+ Add</button>
                        </div>
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--text-3)', marginBottom: 20 }}>These documents are shown to visitors in the visa detail popup on the website.</p>
                </div>

                <div className="admin-modal-footer">
                    <button className="admin-btn admin-btn-secondary" onClick={onClose}>Cancel</button>
                    <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <><i className="fa-solid fa-circle-notch fa-spin" /> Saving...</> : <><i className="fa-solid fa-floppy-disk" /> Save</>}
                    </button>
                </div>
            </div>
        </div>
    );
});

/* ─── MAIN TAB ────────────────────────────────────────────────────────────── */
const VisaTab = ({ showToast }) => {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null); // null | { initialForm, isNew }
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [search, setSearch] = useState('');
    const [filterRegion, setFilterRegion] = useState('all');

    useEffect(() => { fetchCards(); }, []);

    const fetchCards = async () => {
        setLoading(true);
        const { data } = await supabase.from('visa_cards').select('*').order('display_order');
        setCards(data || []);
        setLoading(false);
    };

    const openAdd = () => setModal({ initialForm: { ...emptyVisa, display_order: cards.length }, isNew: true });
    const openEdit = (card) => setModal({ initialForm: { ...card, docs: card.docs || [], notes: card.notes || '' }, isNew: false });

    const handleSave = async (form, isNew) => {
        if (!form.country || !form.flag) { showToast('Country name and Flag code are required', 'error'); return; }
        const payload = { ...form };
        const op = isNew
            ? supabase.from('visa_cards').insert([payload])
            : supabase.from('visa_cards').update(payload).eq('id', form.id);
        const { error } = await op;
        if (!error) { showToast(isNew ? 'Visa added!' : 'Visa updated!', 'success'); fetchCards(); setModal(null); }
        else showToast('Error: ' + error.message, 'error');
    };

    const deleteCard = async (id) => {
        const { error } = await supabase.from('visa_cards').delete().eq('id', id);
        if (!error) { showToast('Deleted!', 'success'); fetchCards(); setDeleteConfirm(null); }
        else showToast('Error: ' + error.message, 'error');
    };

    const filtered = cards.filter(c => {
        const matchSearch = !search || c.country.toLowerCase().includes(search.toLowerCase()) || c.region.toLowerCase().includes(search.toLowerCase());
        const matchRegion = filterRegion === 'all' || c.region === filterRegion;
        return matchSearch && matchRegion;
    });

    const regions = [...new Set(cards.map(c => c.region))];

    return (
        <div className="admin-tab-content">
            {/* Stats */}
            <div className="admin-stats-row">
                <div className="admin-stat-card">
                    <div className="admin-stat-icon gold"><i className="fa-solid fa-passport" /></div>
                    <div><div className="admin-stat-val">{cards.length}</div><div className="admin-stat-lbl">Total Countries</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon blue"><i className="fa-solid fa-fire" /></div>
                    <div><div className="admin-stat-val">{cards.filter(c => c.popular).length}</div><div className="admin-stat-lbl">Popular (shown first)</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon green"><i className="fa-solid fa-gift" /></div>
                    <div><div className="admin-stat-val">{cards.filter(c => !c.fee_inr || c.fee_inr === 0).length}</div><div className="admin-stat-lbl">Free Entry</div></div>
                </div>
                <div className="admin-stat-card">
                    <div className="admin-stat-icon purple"><i className="fa-solid fa-layer-group" /></div>
                    <div><div className="admin-stat-val">{regions.length}</div><div className="admin-stat-lbl">Regions</div></div>
                </div>
            </div>

            {/* Header */}
            <div className="admin-section-header">
                <div className="admin-section-title"><i className="fa-solid fa-passport" /> Visa Countries</div>
                <div style={{ display: 'flex', gap: 10 }}>
                    <div className="admin-search-wrap">
                        <i className="fa-solid fa-search" />
                        <input className="admin-search" placeholder="Search country or region…" value={search} onChange={e => setSearch(e.target.value)} />
                    </div>
                    <button className="admin-btn admin-btn-primary" onClick={openAdd}>
                        <i className="fa-solid fa-plus" /> Add Country
                    </button>
                </div>
            </div>

            {/* Region filter */}
            <div className="admin-filter-bar" style={{ marginBottom: 16 }}>
                <button className={`admin-filter-btn ${filterRegion === 'all' ? 'active' : ''}`} onClick={() => setFilterRegion('all')}>
                    All ({cards.length})
                </button>
                {regions.map(r => (
                    <button key={r} className={`admin-filter-btn ${filterRegion === r ? 'active' : ''}`} onClick={() => setFilterRegion(r)}>
                        {r} ({cards.filter(c => c.region === r).length})
                    </button>
                ))}
            </div>

            {/* Info */}
            <div style={{ background: '#eff6ff', border: '1px solid rgba(37,99,235,0.15)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#2563eb', marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                <i className="fa-solid fa-circle-info" />
                <span>Cards marked as <strong>Popular</strong> appear in the featured section on the <strong>/visa</strong> website page. All cards are visible in the full list.</span>
            </div>

            {/* Cards Grid */}
            {loading ? (
                <div className="admin-loading"><div className="admin-spinner" /><p>Loading visa countries…</p></div>
            ) : filtered.length === 0 ? (
                <div className="admin-empty"><i className="fa-solid fa-passport" /><p>No visa countries yet. Click "Add Country" to start.</p></div>
            ) : (
                <div className="admin-card-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
                    {filtered.map(card => (
                        <div key={card.id} className="admin-visa-card" style={{ borderTop: `4px solid ${card.color || '#0ea5e9'}` }}>
                            {/* Card Top */}
                            <div className="admin-visa-card-top">
                                <FlagImg code={card.flag} size={40} />
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="admin-visa-country">{card.country}</div>
                                    <div className="admin-visa-region">{card.region}</div>
                                </div>
                                {card.popular && (
                                    <span className="badge badge-popular"><i className="fa-solid fa-fire" /> Popular</span>
                                )}
                            </div>

                            {/* Card Body — full detail */}
                            <div className="admin-visa-card-body">
                                {/* Type badge */}
                                <div style={{ marginBottom: 10 }}>
                                    <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 5,
                                        padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                                        background: `${card.color}18`, color: card.color, border: `1px solid ${card.color}33`
                                    }}>
                                        <i className="fa-solid fa-tag" /> {card.type}
                                    </span>
                                </div>

                                {/* Key details grid */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px 12px', marginBottom: 10 }}>
                                    <div>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Processing</div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>{card.processing || '—'}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Validity</div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>{card.validity || '—'}</div>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Entries</div>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)' }}>{card.entries || '—'}</div>
                                    </div>
                                </div>

                                {/* Fee */}
                                {(card.fee_inr > 0 || card.fee > 0) && (
                                    <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
                                        {card.fee_inr > 0 && (
                                            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
                                                <i className="fa-solid fa-indian-rupee-sign" style={{ marginRight: 3, color: 'var(--gold)' }} />
                                                ₹{card.fee_inr.toLocaleString()} INR
                                            </span>
                                        )}
                                        {card.fee > 0 && card.currency && (
                                            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
                                                <i className="fa-solid fa-coins" style={{ marginRight: 3, color: 'var(--text-3)' }} />
                                                {card.fee} {card.currency}
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* Required documents */}
                                {(card.docs || []).length > 0 && (
                                    <div style={{ marginBottom: 8 }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                                            Required Documents ({card.docs.length})
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            {card.docs.slice(0, 4).map((doc, i) => (
                                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-2)' }}>
                                                    <i className="fa-solid fa-check" style={{ color: 'var(--green)', fontSize: 9, flexShrink: 0 }} />
                                                    {doc}
                                                </div>
                                            ))}
                                            {card.docs.length > 4 && (
                                                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>+{card.docs.length - 4} more…</div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Notes */}
                                {card.notes && (
                                    <div style={{ fontSize: 11, color: 'var(--text-3)', background: 'var(--bg-hover)', borderRadius: 6, padding: '6px 10px', marginBottom: 4 }}>
                                        <i className="fa-solid fa-note-sticky" style={{ marginRight: 5 }} />{card.notes}
                                    </div>
                                )}
                            </div>

                            {/* Card Actions */}
                            <div className="admin-visa-card-actions">
                                <button className="admin-btn admin-btn-secondary admin-btn-sm" style={{ flex: 1 }} onClick={() => openEdit(card)}>
                                    <i className="fa-solid fa-pen" /> Edit All Details
                                </button>
                                <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setDeleteConfirm(card.id)}>
                                    <i className="fa-solid fa-trash" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
                    <div className="admin-modal" style={{ maxWidth: 400 }} onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h3><i className="fa-solid fa-triangle-exclamation" /> Confirm Delete</h3>
                            <button className="admin-modal-close" onClick={() => setDeleteConfirm(null)}><i className="fa-solid fa-xmark" /></button>
                        </div>
                        <div className="admin-modal-body">
                            <p style={{ color: 'var(--text-2)', fontSize: 14 }}>Are you sure you want to delete this visa country? It will no longer appear on the website.</p>
                        </div>
                        <div className="admin-modal-footer">
                            <button className="admin-btn admin-btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
                            <button className="admin-btn admin-btn-danger" onClick={() => deleteCard(deleteConfirm)}>Delete</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit/Add Modal */}
            {modal && (
                <VisaEditModal
                    initialForm={modal.initialForm}
                    isNew={modal.isNew}
                    onSave={handleSave}
                    onClose={() => setModal(null)}
                />
            )}
        </div>
    );
};

export default VisaTab;
